import { useQuery } from '@tanstack/react-query';
import { Globe, ExternalLink, MapPin, ArrowUpRight } from 'lucide-react';
import { getSiteUrl } from '@/lib/siteUrl';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface MemberSite {
  id: string;
  subdomain: string;
  custom_domain: string | null;
  appraiser_name: string | null;
  company_name: string | null;
  company_logo_url: string | null;
  city: string | null;
  state: string | null;
  slug: string | null;
}

export function DirectoryMemberSites() {
  const { data: sites, isLoading } = useQuery({
    queryKey: ['directory-member-sites'],
    queryFn: async () => {
      const { data: siteRows, error } = await supabase
        .from('appraiser_sites')
        .select('id, subdomain, custom_domain, user_id')
        .eq('is_published', true);

      if (error || !siteRows || siteRows.length === 0) return [];

      const userIds = siteRows.map(s => s.user_id);

      const { data: agents } = await supabase
        .from('agents')
        .select('user_id, name, city, state, company_id, slug')
        .in('user_id', userIds);

      const agentMap = new Map(
        (agents || []).map(a => [a.user_id, a])
      );

      const companyIds = (agents || []).map(a => a.company_id).filter(Boolean) as string[];
      let companyMap = new Map<string, { name: string; logo_url: string | null }>();
      if (companyIds.length > 0) {
        const { data: companies } = await supabase
          .from('companies')
          .select('id, name, logo_url')
          .in('id', companyIds);
        companyMap = new Map(
          (companies || []).map(c => [c.id, { name: c.name, logo_url: c.logo_url }])
        );
      }

      return siteRows.map(site => {
        const agent = agentMap.get(site.user_id);
        const company = agent?.company_id ? companyMap.get(agent.company_id) : null;
        return {
          id: site.id,
          subdomain: site.subdomain,
          custom_domain: site.custom_domain,
          appraiser_name: agent?.name || null,
          company_name: company?.name || null,
          company_logo_url: company?.logo_url || null,
          city: agent?.city || null,
          state: agent?.state || null,
          slug: agent?.slug || null,
        } as MemberSite;
      });
    },
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading || !sites || sites.length === 0) return null;

  return (
    <section className="container mx-auto px-4 py-12">
      <div className="text-center mb-10">
        <Badge variant="outline" className="mb-4 text-xs font-medium border-primary/30 text-primary">
          <Globe className="h-3 w-3 mr-1" />
          Powered by Ashlar App
        </Badge>
        <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-3">
          Member Websites
        </h2>
        <p className="text-muted-foreground max-w-xl mx-auto">
          Professional appraiser websites built and managed on our platform. Visit any site to learn more about our members.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {sites.map(site => {
          const displayName = site.company_name || site.appraiser_name || site.subdomain;
          const initial = (displayName)[0]?.toUpperCase() || 'A';
          const location = [site.city, site.state].filter(Boolean).join(', ');
          const displayDomain = site.custom_domain || `${site.subdomain}.ashlarapp.com`;

          return (
            <a
              key={site.id}
              href={getSiteUrl(site.subdomain)}
              target="_blank"
              rel="noopener noreferrer"
              className="block group"
            >
              <Card className="h-full overflow-hidden border-border/60 hover:border-primary/40 hover:shadow-lg hover:shadow-primary/5 transition-all duration-300">
                {/* Gradient header bar */}
                <div className="h-2 bg-gradient-to-r from-primary to-primary/60" />
                <CardContent className="p-5">
                  <div className="flex items-start gap-4">
                    {/* Company logo or initial */}
                    <div className="h-14 w-14 rounded-xl bg-primary/10 border border-border/50 flex items-center justify-center flex-shrink-0 overflow-hidden group-hover:border-primary/30 transition-colors">
                      {site.company_logo_url ? (
                        <img
                          src={site.company_logo_url}
                          alt={displayName}
                          className="h-full w-full object-contain p-1.5"
                        />
                      ) : (
                        <span className="text-xl font-bold text-primary">{initial}</span>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                          {displayName}
                        </h3>
                        <ArrowUpRight className="h-4 w-4 text-muted-foreground/0 group-hover:text-primary transition-all -translate-x-1 group-hover:translate-x-0 flex-shrink-0" />
                      </div>
                      {site.company_name && site.appraiser_name && (
                        <p className="text-sm text-muted-foreground truncate mt-0.5">
                          {site.appraiser_name}
                        </p>
                      )}
                      {location && (
                        <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                          <MapPin className="h-3 w-3 flex-shrink-0" />
                          {location}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Domain footer */}
                  <div className="mt-4 pt-3 border-t border-border/40 flex items-center gap-1.5">
                    <Globe className="h-3.5 w-3.5 text-primary/60 flex-shrink-0" />
                    <span className="text-xs text-muted-foreground truncate font-medium">
                      {displayDomain}
                    </span>
                    <ExternalLink className="h-3 w-3 text-muted-foreground/40 ml-auto flex-shrink-0 group-hover:text-primary transition-colors" />
                  </div>
                </CardContent>
              </Card>
            </a>
          );
        })}
      </div>
    </section>
  );
}
